import { Button, Form, Input, message } from 'antd';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './quillEditor.css'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Item, ItemService } from '../../services/Item';
import { ImageActions } from '@xeger/quill-image-actions';
import { ImageFormats } from '@xeger/quill-image-formats';
import { API_URL } from '../../const';
import QuillImageDropAndPaste from 'quill-image-drop-and-paste'
import TableForm from '../items/tableForm';
import { Tool, ToolService } from '../../services/Tool';
import TableFormDoDont from '../items/tableFormDoDont';
import { ItemsContext } from '../../layout/itemContext/itemContext';

Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste)
Quill.register('modules/imageActions', ImageActions);
Quill.register('modules/imageFormats', ImageFormats);
// var SizeStyle = Quill.import('attributors/style/size');
// SizeStyle.whitelist = ['false','12px', '14px', '16px', '18px', '20px'];
// Quill.register(SizeStyle, true);

const QuillEditor = ({ item }: { item?: Item }) => {
    const [value, setValue] = useState(item?.content || '');
    const params = useParams();
    const quillRef = useRef<ReactQuill>();
    const navigate = useNavigate();
    const [title, setTitle] = useState(item?.title || '');
    const { state } = useLocation();
    const [form] = Form.useForm();
    const [tools, setTools] = useState<Tool[]>([]);
    const country = params.country;
    const { getTitles } = useContext(ItemsContext);

    useEffect( () => {
        const getTools = async () => {
            const {data} = await ToolService.getAll(params.country)
            setTools(data)
        }
        getTools();
        
    }, [params.country])

    const handleInputChange = (e: any) => {
        setTitle(e.target.value);
    };

    async function handler(values: any) {
        if (item) {
            await ItemService.update({ ...item, _id: params.id!, content: value, title: title || '', ...values })
            message.success('Item successfully updated!')
            await getTitles()
            navigate(`/${item?.country}/${item?._id}`, { replace: true })
        }
        else {
            const { data } = await ItemService.create({
                parent: state?.parent,
                country: params.country!,
                title,
                content: value,
                children: [],
                itemType: 'content',
                ...values
            });
            message.success('Item successfully added!')
            await getTitles()
            navigate(`/${data?.country}/${data?._id}`, { replace: true })
        }


    }

    async function imagePasteHandler(imageDataUrl: any, type: any, imageData: any) {
        const blob = imageData.toBlob()
        const file = imageData.toFile()
        if (quillRef.current && quillRef.current.getEditor) {
            const reader = new FileReader();

            const formData = new FormData()
            formData.append('avatar', file);

            let options = {
                method: 'POST',
                body: formData,
            };

            const response = await fetch(`${API_URL}/items/upload/`, options)
            const json = (await response.json()) as { url: string }
            const quillEditor = quillRef?.current?.getEditor();
            const range = quillEditor.getSelection(true);
            quillEditor.insertEmbed(range.index, "image", json.url, "user");
        }


    }

    const imageHandler = useCallback(() => {

        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();

        input.onchange = () => {

            if (input.files && input?.files[0]) {
                const file = input.files[0];
                const reader = new FileReader();

                reader.onload = async () => {

                    if (quillRef.current && quillRef.current.getEditor) {
                        const quillEditor = quillRef?.current?.getEditor();

                        const range = quillEditor.getSelection(true);

                        let formData = new FormData();
                        formData.append('avatar', file);

                        let options = {
                            method: 'POST',
                            body: formData,
                        };

                        const response = await fetch(`${API_URL}/items/upload/`, options)
                        const json = (await response.json()) as { url: string }
                        quillEditor.insertEmbed(range.index, "image", json.url, "user");
                    }

                };

                reader.readAsDataURL(file);
            }
        };
    }, []);

    const modules = useMemo(
        () => ({
            imageActions: {},
            imageFormats: {},
            imageDropAndPaste: {
                handler: imagePasteHandler,
            },
            toolbar: {
                container: [
                    [{ header: [1, 2, 3, 4, false] }],
                    ["bold", "italic", "underline"],
                    [{ 'size': ['small', false, 'large', 'huge'] }],
                    // [{ 'size': ['false','12px', '14px', '16px', '18px', '20px'] }],
                    [{ color: [] }],
                    [
                        { list: "ordered" },
                        { list: "bullet" },
                        { indent: "-1" },
                        { indent: "+1" },
                    ],
                    ["link", "image"],
                    [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }]
                ],
                handlers: {
                    image: imageHandler,
                },
            },
            clipboard: {
                matchVisual: false,
            },

        }),
        [imageHandler]
    );

    const formats = [
        "header",
        "bold",
        "italic",
        "underline",
        "strike",
        "list",
        "bullet",
        "indent",
        "link",
        "image",
        "color",
        "width",
        "align",
        "float",
        "size"
    ];

    return <div className='quill-editor' >
        <Input placeholder="Title" onChange={handleInputChange} defaultValue={title} style={{ marginBottom: 16 }} />
        <ReactQuill
            ref={(el: any) => (quillRef.current = el)}
            theme="snow"
            value={value}
            formats={formats}
            modules={modules}
            onChange={(value: any) => { setValue(value) }}
            bounds={'.quill-editor'}
            style={{ border: '2px solid #eee', borderRadius: '10px' }}
        />
        <Form
            name="basic"
            form={form}
            onFinish={handler}
            className='add-item'
            layout="vertical"
            initialValues={{ tableContent: item?.tableContent, tableDoDont: item?.tableDoDont, titleDoDont:item?.titleDoDont }}
            style={{ padding: 0 }}
        >
            <TableForm name='tableContent' title='' tools={tools} />
            <Form.Item name='titleDoDont'>
                <Input placeholder="Title for DOs and DON'Ts table"></Input>
            </Form.Item>
            <TableFormDoDont name='tableDoDont' title=''/>

            <Form.Item>
                <Button type="primary" htmlType="submit" className="form-submit-button">
                    SAVE
                </Button>
            </Form.Item>
        </Form>
    </div>
}

export default QuillEditor;